import {ModalBody, ModalFooter, ModalHeader} from "@heroui/modal";
import {Button} from "@heroui/button";
import React, {SVGProps, useEffect} from "react";
import {useMonitoring} from "@/components/monitoring";
import {STARTER_PACK_PRICE, STARTER_PACK_DOWNLOAD_CREDITS, formatPrice} from "@/config/pricing";
import {UpsellVodPreview} from "@/components/DownloaderApp/UpsellVodPreview";

function OutlineCheckCircle(props: SVGProps<SVGSVGElement>) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20px"
            height="20px"
            {...props}
        >
            <path
                fill="currentColor"
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4l8-8z"
            ></path>
        </svg>
    )
}

const PremiumSvgIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        className="size-3 shrink-0 grow-0"
        fill="currentColor"
        height="32"
        viewBox="0 0 32 32"
        width="32"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <path
            d="M17.4999 11.2575L18.6459 13.7338C19.1794 14.8888 20.1067 15.8163 21.2613 16.3501L23.7369 17.4876L21.2613 18.6339C20.1067 19.1676 19.1794 20.0952 18.6459 21.2502L17.4999 23.7265L16.354 21.2502C15.8204 20.0952 14.8932 19.1676 13.7386 18.6339L11.263 17.4876L13.7386 16.3413C14.8932 15.8076 15.8204 14.8801 16.354 13.7338L17.4999 11.2575ZM17.4999 4.9924L13.9747 12.6313C13.7036 13.2175 13.2312 13.6901 12.6451 13.9613L5.00863 17.4876L12.6539 21.0139C13.24 21.2852 13.7123 21.7577 13.9835 22.3439L17.5087 29.9828L21.0339 22.3439C21.3051 21.7577 21.7774 21.2852 22.3635 21.0139L30 17.4876L22.3635 13.9613C21.7774 13.6901 21.3051 13.2175 21.0339 12.6313L17.5087 4.9924H17.4999Z"></path>
        <path
            d="M7.37091 2L5.85761 5.29006C5.74389 5.54381 5.5427 5.74506 5.28903 5.85882L2 7.37259L5.28903 8.88637C5.5427 9.00012 5.74389 9.20137 5.85761 9.45513L7.37091 12.7364L8.88422 9.44638C8.99793 9.19262 9.19912 8.99137 9.4528 8.87762L12.7418 7.36384L9.4528 5.85007C9.19912 5.73631 8.99793 5.53506 8.88422 5.28131L7.37091 2Z"></path>
    </svg>
);

function TicketIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
        >
            <path d="M3 7a2 2 0 012-2h14a2 2 0 012 2v2a3 3 0 000 6v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2a3 3 0 000-6V7z"/>
        </svg>
    );
}

export function UpsellPopupVariant1({data, onClose}) {
    const {track} = useMonitoring()

    const [isLoading, setIsLoading] = React.useState(false)

    const hasVodContext = Boolean(data.vodId)
    const pricePerVod = formatPrice(STARTER_PACK_PRICE / STARTER_PACK_DOWNLOAD_CREDITS)
    const creditsLeftAfterThisVod = STARTER_PACK_DOWNLOAD_CREDITS - 1
    const resumesDownload = hasVodContext && Boolean(data.vodUrl) && !data.isExternal

    useEffect(() => {
        track('UpsellPopup opened', {
            source: data.source,
            variant: 'variant1',
            hasVodContext
        })
    }, []);

    useEffect(() => {
        try {
            window.posthog?.startSessionRecording();
        } catch (err) {
        }

        return () => {
            try {
                window.posthog?.stopSessionRecording();
            } catch (err) {
            }
        }
    }, []);

    const CTA_LINK = `/api/stripe/checkout?platform=kick${data.vodUrl ? `&vodUrl=${data.vodUrl}` : ''}`;

    function onPressed() {
        track('[Premium] UpsellPopup > CTA clicked', {
            source: data.source,
            variant: 'variant1',
            hasVodContext
        });
        setIsLoading(true);
        if (data.isExternal) {
            window.open(CTA_LINK);
            onClose();
        } else {
            window.location.href = CTA_LINK;
        }
    }

    const benefits = [
        hasVodContext
            ? <>This VOD costs <b className="text-white">1 credit</b>. The other <b
                className="text-white">{creditsLeftAfterThisVod}</b> stay on your account.</>
            : <><b className="text-white">{STARTER_PACK_DOWNLOAD_CREDITS} VODs</b> from any Kick creator, subscriber-only
                or private.</>,
        <>Grabbing the same VOD again is free. It never costs a second credit.</>,
        <>Built-in player, so you can watch a VOD without downloading the full file.</>,
        <>One payment. Nothing renews and there is no sub to cancel.</>,
    ];

    return (
        <>
            <ModalHeader className="flex flex-col items-center gap-2 px-5 pb-3 pt-6 text-center sm:px-8">
                <h2 id="modal-title" className="max-w-xl text-xl font-bold leading-snug sm:text-2xl">
                    {hasVodContext
                        ? <>Download or watch this VOD without a monthly subscription</>
                        : <>Download or watch subscriber-only VODs without a monthly subscription</>}
                </h2>
                <p className="max-w-md pb-2 pt-1 text-sm font-normal text-neutral-400">
                    Get <b className="font-semibold text-white">{STARTER_PACK_DOWNLOAD_CREDITS} download credits</b> for
                    just <b className="font-semibold text-white">{formatPrice(STARTER_PACK_PRICE)}</b>
                </p>
            </ModalHeader>

            <ModalBody className="px-5 pb-4 pt-1 sm:px-8">
                <div
                    className={`grid gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 ${hasVodContext ? 'sm:grid-cols-[216px_1fr] sm:items-center' : ''}`}>
                    <UpsellVodPreview
                        className="mx-auto"
                        showPlaceholder
                        vodId={data.vodId}
                        vodUrl={data.vodUrl}
                        width={216}
                    />
                    <ul className="flex flex-col gap-3 text-sm text-neutral-300">
                        {benefits.map((benefit, i) => (
                            <li key={i} className="flex items-start gap-2.5">
                                <OutlineCheckCircle className="mt-0.5 shrink-0 text-[#51fc1a]"/>
                                <span>{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </ModalBody>

            <ModalFooter
                className="flex flex-col gap-3 border-t border-white/10 bg-background/80 px-5 pb-5 pt-4 backdrop-blur sm:px-8">
                <div
                    className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <div className="text-left">
                        <p className="flex items-center gap-2 text-base font-semibold leading-tight text-white">
                            <TicketIcon className="size-4 shrink-0 text-[#51fc1a]"/>
                            {STARTER_PACK_DOWNLOAD_CREDITS} download credits
                        </p>
                        <p className="mt-1.5 text-xs text-neutral-400">
                            One-time payment, <b className="font-semibold text-white">{pricePerVod} a VOD</b>, valid for
                            3 months
                        </p>
                    </div>
                    <div className="shrink-0 text-right">
                        <p className="text-3xl font-bold leading-none text-white">
                            {formatPrice(STARTER_PACK_PRICE)}
                        </p>
                        <p className="mt-1.5 text-[11px] font-medium uppercase tracking-wider text-[#51fc1a]">
                            one-time
                        </p>
                    </div>
                </div>

                <Button
                    className="w-full text-base font-semibold"
                    color="primary"
                    isDisabled={isLoading}
                    isLoading={isLoading}
                    onPress={onPressed}
                    size="lg"
                    startContent={isLoading ? null : <PremiumSvgIcon className="size-5 shrink-0 grow-0"/>}
                >
                    <span>
                        {hasVodContext
                            ? 'Unlock this VOD'
                            : `Get ${STARTER_PACK_DOWNLOAD_CREDITS} download credits`}
                    </span>
                </Button>

                <p className="text-center text-xs text-neutral-400">
                    {resumesDownload
                        ? <>Secure Stripe checkout. You land back here and the download picks up where it stopped.</>
                        : <>Secure Stripe checkout. Your credits are ready the second the payment goes through.</>}
                </p>
            </ModalFooter>
        </>
    );
}
